import React, { useState } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormDatas } from "../../types/FormData";

interface DocumentUploadProps {
  formData: FormDatas;
  setFormData: (data: FormDatas) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  formData,
  setFormData,
}) => {
  const [dragging, setDragging] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const newDocuments: { name: string; content: string }[] = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = reader.result as string;
        newDocuments.push({ name: file.name, content: base64String });

        // Stellen Sie sicher, dass der Zustand nach dem Einlesen aller Dateien aktualisiert wird
        if (newDocuments.length === files.length) {
          setFormData({
            ...formData,
            documents: [...formData.documents, ...newDocuments],
          });
        }
      };
    });
  };

  const handleRemoveFile = (index: number) => {
    const updatedDocuments = formData.documents.filter((_, i) => i !== index);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const files = event.dataTransfer.files;
    handleFileChange({ target: { files } } as any);
  };

  return (
    <Box>
      <Typography variant="h2" sx={{ textAlign: "center", mb: 2 }}>
        Erforderliche Dokumente hochladen
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
  Bitte laden Sie folgende Unterlagen hoch:<br />
  1. Kopie vom Sozialbescheid<br />
  2. But-Kartennummer, falls vorhanden.
</Typography>


      {/* Drag-and-Drop Bereich */}
      <Box
        sx={{
          border: "2px dashed #1976d2",
          borderRadius: "8px",
          padding: 4,
          textAlign: "center",
          backgroundColor: dragging ? "rgba(25, 118, 210, 0.1)" : "transparent",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Typography variant="h6" sx={{ color: "#1976d2", mb: 1 }}>
          <AddIcon sx={{ fontSize: 40 }} />
        </Typography>
        <Typography variant="body2" sx={{ color: "#1976d2", mb: 1 }}>
          Drag & Drop oder
        </Typography>
        <Button
          variant="outlined"
          component="label"
          sx={{
            border: "2px solid #1976d2",
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
          }}
        >
          Durchsuchen
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>
      </Box>

      {/* Unterstützte Dateiformate */}
      <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
        Unterstützte Formate: JPG, JPEG, PNG
      </Typography>

      {/* Anzeigen von hochgeladenen Dateien */}
      {formData.documents.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {formData.documents.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f0f0f0",
                borderRadius: 1,
                padding: "8px 12px",
                marginBottom: 2,
              }}
            >
              <Typography sx={{ mr: 1 }}>{file.name}</Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveFile(index)}
                sx={{ color: "#f44336" }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DocumentUpload;
